import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Building2, Check, Info, QrCode } from 'lucide-react';
import { useLang } from '../context/LangContext';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import './WhatsApp.css';

const OFFICIAL_WA_STORAGE_KEY = 'replytek_official_wa_connected';

const META_APP_ID =
  process.env.REACT_APP_META_APP_ID || '1492692442348780';
const META_CONFIG_ID =
  process.env.REACT_APP_META_CONFIG_ID || '1272138658369514';

const METHOD_DISCLAIMER = {
  en: 'Important: The same phone number cannot be connected through both methods at the same time. If you need to switch methods, disconnect the current profile first.',
  es: 'Importante: El mismo número de teléfono no puede estar conectado por ambos métodos al mismo tiempo. Si necesitas cambiar de método, desconecta el perfil actual primero.',
};

const CONNECT_METHOD_INTRO = {
  en: {
    title: 'Choose how to connect',
    subtitle:
      'Pick the option that works best for your business. You can add more profiles later.',
  },
  es: {
    title: 'Elige cómo conectar',
    subtitle:
      'Selecciona la opción que mejor funcione para tu negocio. Puedes agregar más perfiles después.',
  },
};

const METHOD_MODAL_TITLE = {
  en: 'Add a connection',
  es: 'Agregar una conexión',
};

const METHOD_BADGE_RECOMMENDED = {
  en: 'RECOMMENDED',
  es: 'RECOMENDADO',
};

const METHOD_CARD_API = {
  en: {
    title: 'WhatsApp Business API',
    tagline: 'Official Channel',
    desc: "Connect your business through Meta's official WhatsApp API. Enterprise-grade reliability with no phone required to stay online.",
    features: [
      'Official Meta integration',
      '24/7 uptime — no phone needed',
      'Business verified profile',
      'Scalable for high volume',
    ],
    btnConnect: 'Connect with WhatsApp Business',
    btnConnecting: 'Connecting…',
  },
  es: {
    title: 'WhatsApp Business API',
    tagline: 'Canal Oficial',
    desc:
      'Conecta tu negocio a través de la API oficial de WhatsApp de Meta. Estabilidad empresarial sin necesidad de mantener un teléfono conectado.',
    features: [
      'Integración oficial de Meta',
      'Activo 24/7 — sin teléfono necesario',
      'Perfil de negocio verificado',
      'Escalable para alto volumen',
    ],
    btnConnect: 'Conectar con WhatsApp Business',
    btnConnecting: 'Conectando…',
  },
};

const METHOD_CARD_QR = {
  en: {
    title: 'WhatsApp QR',
    tagline: 'Direct Connection',
    desc:
      'Link your existing WhatsApp number instantly by scanning a QR code. Works with any WhatsApp number — personal or business.',
    features: [
      'Connect in under 60 seconds',
      'Works with any WhatsApp number',
      'No additional setup required',
      'Uses your existing WhatsApp app',
    ],
    btnConnect: 'Connect with QR Code',
  },
  es: {
    title: 'WhatsApp QR',
    tagline: 'Conexión Directa',
    desc:
      'Vincula tu número de WhatsApp al instante escaneando un código QR. Funciona con cualquier número de WhatsApp — personal o de negocio.',
    features: [
      'Conecta en menos de 60 segundos',
      'Funciona con cualquier número',
      'Sin configuración adicional',
      'Usa tu app de WhatsApp existente',
    ],
    btnConnect: 'Conectar con QR',
  },
};

const QR_CONFLICT_HELP = {
  en: 'Go to your WhatsApp profiles below, tap Disconnect on the QR session that uses this number, then try connecting the official API again.',
  es: 'Ve a tus perfiles de WhatsApp abajo, pulsa Desconectar en la sesión QR que usa ese número y vuelve a intentar conectar la API oficial.',
};

const OFFICIAL_ACTIVE_QR_WARNING = {
  en: 'You already have an official WhatsApp Business API connection. If you connect the same number by QR, WhatsApp may disconnect one of the sessions. Use a different number or disconnect the official API first.',
  es: 'Ya tienes una conexión oficial de WhatsApp Business API. Si conectas el mismo número por QR, WhatsApp puede desconectar una de las sesiones. Usa otro número o desconecta primero la API oficial.',
};

const OFFICIAL_DISCONNECT_CONFIRM = {
  en: 'Are you sure you want to disconnect the WhatsApp Business API? This will deregister your phone number from the official API. Your number will return to normal WhatsApp use.',
  es: '¿Seguro que quieres desconectar la API de WhatsApp Business? Esto dará de baja tu número en la API oficial. Tu número volverá al uso normal de WhatsApp.',
};

const OFFICIAL_CARD_FALLBACK_TITLE = {
  en: 'WhatsApp Business',
  es: 'WhatsApp Business',
};

function generateId(uid) {
  return uid + '_' + Math.random().toString(36).substring(2, 8);
}

function readOfficialApiConnected() {
  try {
    return localStorage.getItem(OFFICIAL_WA_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export default function WhatsApp() {
  const { lang } = useLang();
  const { user } = useAuth();
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [qrConnectMode, setQrConnectMode] = useState(
    typeof window !== 'undefined' && window.innerWidth < 768 ? 'pair' : 'qr',
  );
  const [connectUniqueId, setConnectUniqueId] = useState('');
  const [pairPhone, setPairPhone] = useState('');
  const [pairCode, setPairCode] = useState('');
  const [pairExpiresIn, setPairExpiresIn] = useState(0);
  const [pairLoading, setPairLoading] = useState(false);
  const [showConnectMethodModal, setShowConnectMethodModal] = useState(false);
  const [officialApiConnected, setOfficialApiConnected] = useState(readOfficialApiConnected);
  const [fbReady, setFbReady] = useState(false);
  const [metaFlowLoading, setMetaFlowLoading] = useState(false);
  const [metaBanner, setMetaBanner] = useState(null);
  const [officialApiStatus, setOfficialApiStatus] = useState({
    loaded: false,
    official_api_active: false,
    display_phone_number: null,
    business_phone_number_id: null,
    business_name: null,
    waba_id: null,
  });
  const [disconnectingOfficial, setDisconnectingOfficial] = useState(false);
  const [excludedPhones, setExcludedPhones] = useState([]);
  const [excludedLoading, setExcludedLoading] = useState(false);
  const [newExcludedPhone, setNewExcludedPhone] = useState('');
  const [newExcludedLabel, setNewExcludedLabel] = useState('');
  const [excludedSaving, setExcludedSaving] = useState(false);
  const pollingRef = useRef(null);
  const fbInitStarted = useRef(false);

  const fetchInstances = useCallback(async () => {
    try {
      const res = await api.get('/qr/get_all');
      if (res.data?.success) {
        setInstances(res.data.data || []);
      }
    } catch {}
  }, []);

  const fetchExcludedPhones = useCallback(async () => {
    setExcludedLoading(true);
    try {
      const res = await api.get('/excluded-phones');
      if (res.data?.success && Array.isArray(res.data.data)) {
        setExcludedPhones(res.data.data);
      } else {
        setExcludedPhones([]);
      }
    } catch {
      setExcludedPhones([]);
    }
    setExcludedLoading(false);
  }, []);

  const fetchOfficialStatus = useCallback(async () => {
    try {
      const res = await api.get('/embedded-signup/status');
      if (res.data?.success) {
        setOfficialApiStatus({
          loaded: true,
          official_api_active: !!res.data.official_api_active,
          display_phone_number: res.data.display_phone_number || null,
          business_phone_number_id: res.data.business_phone_number_id || null,
          business_name: res.data.business_name || null,
          waba_id: res.data.waba_id || null,
        });
      } else {
        setOfficialApiStatus((s) => ({ ...s, loaded: true }));
      }
    } catch {
      setOfficialApiStatus((s) => ({ ...s, loaded: true }));
    }
  }, []);

  useEffect(() => {
    fetchInstances().finally(() => setLoading(false));
    fetchOfficialStatus();
    fetchExcludedPhones();

    pollingRef.current = setInterval(() => {
      fetchInstances();
      fetchOfficialStatus();
    }, 5000);
    return () => clearInterval(pollingRef.current);
  }, [fetchInstances, fetchOfficialStatus, fetchExcludedPhones]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === OFFICIAL_WA_STORAGE_KEY || e.key === null) {
        setOfficialApiConnected(readOfficialApiConnected());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    if (!officialApiStatus.loaded) return undefined;
    if (officialApiStatus.official_api_active) {
      try {
        localStorage.setItem(OFFICIAL_WA_STORAGE_KEY, '1');
      } catch {
        /* ignore */
      }
      setOfficialApiConnected(true);
    } else {
      try {
        localStorage.removeItem(OFFICIAL_WA_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      setOfficialApiConnected(false);
    }
    return undefined;
  }, [officialApiStatus.loaded, officialApiStatus.official_api_active]);

  useEffect(() => {
    if (!metaBanner || metaBanner.type !== 'success') return undefined;
    const t = setTimeout(() => setMetaBanner(null), 7000);
    return () => clearTimeout(t);
  }, [metaBanner]);

  useEffect(() => {
    if (fbInitStarted.current) return;
    fbInitStarted.current = true;

    if (!document.getElementById('fb-root')) {
      const root = document.createElement('div');
      root.id = 'fb-root';
      document.body.appendChild(root);
    }

    window.fbAsyncInit = () => {
      if (!window.FB) {
        setMetaBanner({
          type: 'error',
          text: 'Facebook SDK failed to initialize.',
        });
        return;
      }
      window.FB.init({
        appId: META_APP_ID,
        autoLogAppEvents: true,
        xfbml: true,
        version: 'v21.0',
      });
      setFbReady(true);
    };

    const existing = document.getElementById('facebook-jssdk');
    if (existing && window.FB) {
      window.FB.init({
        appId: META_APP_ID,
        autoLogAppEvents: true,
        xfbml: true,
        version: 'v21.0',
      });
      setFbReady(true);
      return undefined;
    }

    const script = document.createElement('script');
    script.id = 'facebook-jssdk';
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    document.body.appendChild(script);

    return undefined;
  }, []);

  const hasActiveQrConnection = instances.some(
    (i) => i.status === 'ACTIVE' || i.status === 'GENERATING',
  );
  const hasAnyQrInstance = instances.length > 0;
  const hasOfficialLine =
    officialApiStatus.official_api_active ||
    (!officialApiStatus.loaded && officialApiConnected);
  const showOfficialProfileCard =
    officialApiStatus.official_api_active ||
    (!officialApiStatus.loaded && officialApiConnected);
  const canDisconnectOfficial =
    officialApiStatus.loaded && officialApiStatus.official_api_active;
  const hasManagedConnections =
    hasActiveQrConnection || hasAnyQrInstance || hasOfficialLine;

  const showInitialMethodScreen = !loading && !hasManagedConnections;
  const showDashboardHeader = hasManagedConnections;

  const openQrTitleModal = useCallback(() => {
    setShowConnectMethodModal(false);
    setQrConnectMode(typeof window !== 'undefined' && window.innerWidth < 768 ? 'pair' : 'qr');
    setConnectUniqueId(generateId(user?.uid));
    setPairCode('');
    setPairPhone('');
    setPairExpiresIn(0);
    setShowAdd(true);
  }, [user?.uid]);

  const handleOfficialSignupClick = useCallback(() => {
    setMetaBanner(null);
    if (!window.FB || !fbReady) {
      setMetaBanner({
        type: 'error',
        text: 'Facebook SDK is still loading. Please try again in a moment.',
      });
      return;
    }

    setMetaFlowLoading(true);

    window.FB.login(
      (response) => {
        void (async () => {
          try {
            if (!response || response.status === 'unknown') {
              return;
            }
            if (!response.authResponse) {
              setMetaBanner({
                type: 'error',
                text: 'Facebook login was cancelled or did not complete.',
              });
              return;
            }

            const authCode = response.authResponse.code;
            if (!authCode) {
              setMetaBanner({
                type: 'error',
                text: 'No authorization code returned from Facebook.',
              });
              return;
            }

            const res = await api.post('/embedded-signup/exchange-token', {
              code: authCode,
            });

            if (res.data?.success) {
              try {
                localStorage.setItem(OFFICIAL_WA_STORAGE_KEY, '1');
              } catch {
                /* ignore */
              }
              setOfficialApiConnected(true);
              setShowConnectMethodModal(false);
              setMetaBanner({
                type: 'success',
                text:
                  res.data?.msg ||
                  'WhatsApp Business API connected successfully.',
              });
              await fetchInstances();
              await fetchOfficialStatus();
            } else {
              const baseMsg =
                res.data?.msg ||
                'Could not complete WhatsApp Business connection.';
              const isQrConflict =
                res.data?.code === 'QR_PHONE_CONFLICT' ||
                String(baseMsg).includes('connected via QR');
              const helpLang = lang === 'en' ? 'en' : 'es';
              setMetaBanner({
                type: 'error',
                text: isQrConflict
                  ? `${baseMsg}\n\n${QR_CONFLICT_HELP[helpLang]}`
                  : baseMsg,
              });
            }
          } catch (err) {
            const data = err?.response?.data;
            const msg =
              data?.msg ||
              err?.message ||
              'Network error while connecting to WhatsApp Business.';
            const isQrConflict =
              data?.code === 'QR_PHONE_CONFLICT' ||
              String(msg).includes('connected via QR');
            const helpLang = lang === 'en' ? 'en' : 'es';
            setMetaBanner({
              type: 'error',
              text: isQrConflict ? `${msg}\n\n${QR_CONFLICT_HELP[helpLang]}` : msg,
            });
          } finally {
            setMetaFlowLoading(false);
          }
        })();
      },
      {
        config_id: META_CONFIG_ID,
        response_type: 'code',
        override_default_response_type: true,
        extras: { setup: {}, featureType: '', sessionInfoVersion: '3' },
      },
    );
  }, [fbReady, fetchInstances, fetchOfficialStatus, lang]);

  const handleAdd = async () => {
    if (!title.trim()) return;
    setAdding(true);
    try {
      const uniqueId = connectUniqueId || generateId(user?.uid);
      const res = await api.post('/qr/gen_qr', { title: title.trim(), uniqueId });
      if (res.data?.success) {
        setTitle('');
        setShowAdd(false);
        await fetchInstances();
        await fetchOfficialStatus();
      } else {
        alert(res.data?.msg || 'Error');
      }
    } catch (err) {
      alert('Connection error');
    }
    setAdding(false);
  };

  const handleGetPairingCode = async () => {
    if (!title.trim() || !pairPhone.trim()) return;
    setPairLoading(true);
    try {
      const uniqueId = connectUniqueId || generateId(user?.uid);
      if (!connectUniqueId) setConnectUniqueId(uniqueId);
      const res = await api.post('/qr/pairing-code', {
        title: title.trim(),
        uniqueId,
        phone: pairPhone.trim(),
      });
      if (res.data?.success && res.data?.data?.code) {
        setPairCode(String(res.data.data.code));
        setPairExpiresIn(Number(res.data.data.expires_in) || 60);
        await fetchInstances();
      } else {
        window.alert(
          res.data?.msg ||
            (lang === 'en'
              ? 'Could not get pairing code. You can still use QR.'
              : 'No se pudo obtener el código. Aún puedes usar QR.'),
        );
      }
    } catch (err) {
      window.alert(
        err?.response?.data?.msg ||
          (lang === 'en'
            ? 'Could not get pairing code. You can still use QR.'
            : 'No se pudo obtener el código. Aún puedes usar QR.'),
      );
    }
    setPairLoading(false);
  };

  useEffect(() => {
    if (pairExpiresIn <= 0) return undefined;
    const t = setInterval(() => {
      setPairExpiresIn((v) => (v > 0 ? v - 1 : 0));
    }, 1000);
    return () => clearInterval(t);
  }, [pairExpiresIn]);

  const handleDelete = async (uniqueId) => {
    const confirmMsg = lang === 'en'
      ? 'Are you sure you want to disconnect this WhatsApp profile?'
      : '¿Estás seguro de que quieres desconectar este perfil de WhatsApp?';
    if (!window.confirm(confirmMsg)) return;

    try {
      await api.post('/qr/del_instance', { uniqueId });
      await fetchInstances();
      await fetchOfficialStatus();
    } catch {}
  };

  const handleDisconnectOfficial = useCallback(async () => {
    const helpLang = lang === 'en' ? 'en' : 'es';
    if (!window.confirm(OFFICIAL_DISCONNECT_CONFIRM[helpLang])) return;

    setMetaBanner(null);
    setDisconnectingOfficial(true);
    try {
      const res = await api.post('/embedded-signup/disconnect');
      if (res.data?.success) {
        try {
          localStorage.removeItem(OFFICIAL_WA_STORAGE_KEY);
        } catch {
          /* ignore */
        }
        setOfficialApiConnected(false);
        setOfficialApiStatus({
          loaded: true,
          official_api_active: false,
          display_phone_number: null,
          business_phone_number_id: null,
          business_name: null,
          waba_id: null,
        });
        await fetchOfficialStatus();
        setMetaBanner({
          type: 'success',
          text:
            res.data?.msg ||
            (helpLang === 'en'
              ? 'WhatsApp Business API disconnected successfully'
              : 'API de WhatsApp Business desconectada correctamente'),
        });
      } else {
        setMetaBanner({
          type: 'error',
          text:
            res.data?.msg ||
            (helpLang === 'en'
              ? 'Could not disconnect WhatsApp Business API.'
              : 'No se pudo desconectar la API de WhatsApp Business.'),
        });
      }
    } catch (err) {
      const msg =
        err?.response?.data?.msg ||
        err?.message ||
        (helpLang === 'en'
          ? 'Network error while disconnecting. Your connection was not removed.'
          : 'Error de red al desconectar. No se eliminó la conexión.');
      setMetaBanner({ type: 'error', text: msg });
    } finally {
      setDisconnectingOfficial(false);
    }
  }, [lang, fetchOfficialStatus]);

  const statusLabel = (status) => {
    const map = {
      ACTIVE: { en: 'Connected', es: 'Conectado' },
      INACTIVE: { en: 'Disconnected', es: 'Desconectado' },
      GENERATING: { en: 'Generating QR...', es: 'Generando QR...' },
    };
    const s = map[status] || { en: status, es: status };
    return { text: s[lang] };
  };

  const statusClassForInstance = (status) => {
    if (status === 'ACTIVE') return 'wa-card__status wa-card__status--active';
    if (status === 'GENERATING') return 'wa-card__status wa-card__status--generating';
    if (status === 'INACTIVE') return 'wa-card__status wa-card__status--inactive';
    return 'wa-card__status wa-card__status--muted';
  };

  const handleAddExcluded = async () => {
    if (!newExcludedPhone.trim()) return;
    setExcludedSaving(true);
    try {
      const res = await api.post('/excluded-phones', {
        phone: newExcludedPhone.trim(),
        label: newExcludedLabel.trim() || undefined,
      });
      if (res.data?.success) {
        setNewExcludedPhone('');
        setNewExcludedLabel('');
        await fetchExcludedPhones();
      } else {
        window.alert(res.data?.message || (lang === 'en' ? 'Could not add number' : 'No se pudo agregar el número'));
      }
    } catch (e) {
      window.alert(e.response?.data?.message || (lang === 'en' ? 'Could not add number' : 'No se pudo agregar el número'));
    }
    setExcludedSaving(false);
  };

  const handleRemoveExcluded = async (id) => {
    const msg =
      lang === 'en'
        ? 'Remove this number from the excluded list?'
        : '¿Quitar este número de la lista de exclusión?';
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/excluded-phones/${id}`);
      await fetchExcludedPhones();
    } catch {
      window.alert(lang === 'en' ? 'Could not remove' : 'No se pudo eliminar');
    }
  };

  const renderConnectionMethodCards = (variant) => {
    const wrapClass =
      variant === 'fullscreen'
        ? 'wa-method-screen'
        : 'wa-method-modal-inner';

    const loc = lang === 'es' ? 'es' : 'en';
    const intro = CONNECT_METHOD_INTRO[loc];
    const apiCard = METHOD_CARD_API[loc];
    const qrCard = METHOD_CARD_QR[loc];

    return (
      <div className={wrapClass}>
        {variant === 'fullscreen' && (
          <div className="wa-method-screen__intro">
            <h2 className="wa-method-screen__title">{intro.title}</h2>
            <p className="wa-method-screen__subtitle">{intro.subtitle}</p>
          </div>
        )}
        <div className="wa-method-cards">
          <article className="wa-method-card wa-method-card--featured">
            <div className="wa-method-card__badge">{METHOD_BADGE_RECOMMENDED[loc]}</div>
            <div className="wa-method-card__icon-wrap wa-method-card__icon-wrap--official">
              <Building2 className="wa-method-card__icon" aria-hidden />
            </div>
            <h3 className="wa-method-card__heading">{apiCard.title}</h3>
            <p className="wa-method-card__tagline">{apiCard.tagline}</p>
            <p className="wa-method-card__desc">{apiCard.desc}</p>
            <ul className="wa-method-card__features">
              {apiCard.features.map((line) => (
                <li key={line}>
                  <Check className="wa-method-card__check" aria-hidden /> {line}
                </li>
              ))}
            </ul>
            <button
              type="button"
              className={`wa-method-card__btn wa-method-card__btn--primary${metaFlowLoading ? ' wa-method-card__btn--busy' : ''}`}
              onClick={handleOfficialSignupClick}
              disabled={metaFlowLoading || !fbReady}
            >
              {metaFlowLoading ? apiCard.btnConnecting : apiCard.btnConnect}
            </button>
          </article>

          <article className="wa-method-card">
            <div className="wa-method-card__icon-wrap wa-method-card__icon-wrap--qr">
              <QrCode className="wa-method-card__icon" aria-hidden />
            </div>
            <h3 className="wa-method-card__heading">{qrCard.title}</h3>
            <p className="wa-method-card__tagline">{qrCard.tagline}</p>
            <p className="wa-method-card__desc">{qrCard.desc}</p>
            <ul className="wa-method-card__features">
              {qrCard.features.map((line) => (
                <li key={line}>
                  <Check className="wa-method-card__check" aria-hidden /> {line}
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="wa-method-card__btn wa-method-card__btn--secondary"
              onClick={openQrTitleModal}
              disabled={metaFlowLoading}
            >
              {qrCard.btnConnect}
            </button>
          </article>
        </div>

        <div className="wa-method-disclaimer" role="note">
          <Info className="wa-method-disclaimer__icon" aria-hidden />
          <p className="wa-method-disclaimer__text">
            {METHOD_DISCLAIMER[loc]}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="wa-page">
      {metaBanner && (
        <div
          className={`wa-meta-banner wa-meta-banner--${metaBanner.type}`}
          role="alert"
        >
          {metaBanner.text}
        </div>
      )}

      {showDashboardHeader && (
        <>
          <div className="wa-header">
            <div>
              <h1>{lang === 'en' ? 'WhatsApp Profiles' : 'Perfiles WhatsApp'}</h1>
              <p className="wa-header__sub">
                {lang === 'en'
                  ? 'Connect your WhatsApp number by scanning a QR code'
                  : 'Conecta tu número de WhatsApp escaneando un código QR'}
              </p>
            </div>
            <button
              type="button"
              className="wa-btn-add"
              onClick={() => setShowConnectMethodModal(true)}
              disabled={metaFlowLoading}
            >
              + {lang === 'en' ? 'Connect New' : 'Conectar Nuevo'}
            </button>
          </div>

          {showConnectMethodModal && (
            <div
              className="wa-modal-overlay"
              onClick={() => !metaFlowLoading && setShowConnectMethodModal(false)}
              role="presentation"
            >
              <div
                className="wa-method-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="wa-method-modal-title"
              >
                <div className="wa-method-modal__head">
                  <h2 id="wa-method-modal-title" className="wa-method-modal__title">
                    {METHOD_MODAL_TITLE[lang === 'es' ? 'es' : 'en']}
                  </h2>
                  <button
                    type="button"
                    className="wa-method-modal__close"
                    onClick={() => !metaFlowLoading && setShowConnectMethodModal(false)}
                    aria-label="Close"
                    disabled={metaFlowLoading}
                  >
                    ×
                  </button>
                </div>
                {renderConnectionMethodCards('modal')}
              </div>
            </div>
          )}
        </>
      )}

      {showInitialMethodScreen && renderConnectionMethodCards('fullscreen')}

      {showAdd && (
        <div className="wa-modal-overlay" onClick={() => setShowAdd(false)} role="presentation">
          <div className="wa-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="wa-modal-title">
            <h2 id="wa-modal-title">{lang === 'en' ? 'Connect WhatsApp' : 'Conectar WhatsApp'}</h2>
            <p className="wa-modal__desc">
              {lang === 'en'
                ? 'Give this profile a name (e.g. "My Business WhatsApp")'
                : 'Dale un nombre a este perfil (ej. "Mi WhatsApp del Negocio")'}
            </p>
            {officialApiStatus.official_api_active && (
              <div className="wa-modal__official-warning" role="status">
                <Info className="wa-modal__official-warning__icon" aria-hidden />
                <div className="wa-modal__official-warning__body">
                  <p>{OFFICIAL_ACTIVE_QR_WARNING[lang === 'en' ? 'en' : 'es']}</p>
                  {officialApiStatus.display_phone_number && (
                    <p className="wa-modal__official-warning__line">
                      {lang === 'en' ? 'Official line: ' : 'Línea oficial: '}
                      <span className="wa-modal__official-warning__num">
                        {officialApiStatus.display_phone_number}
                      </span>
                    </p>
                  )}
                </div>
              </div>
            )}
            <input
              className="wa-modal__input"
              type="text"
              placeholder={lang === 'en' ? 'Profile name' : 'Nombre del perfil'}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && qrConnectMode === 'qr' && handleAdd()}
              autoFocus
            />

            <div className="wa-link-mode">
              <button
                type="button"
                className={`wa-link-mode__tab ${qrConnectMode === 'qr' ? 'wa-link-mode__tab--active' : ''}`}
                onClick={() => setQrConnectMode('qr')}
              >
                {lang === 'en' ? 'Scan QR Code' : 'Escanear QR'}
              </button>
              <button
                type="button"
                className={`wa-link-mode__tab ${qrConnectMode === 'pair' ? 'wa-link-mode__tab--active' : ''}`}
                onClick={() => setQrConnectMode('pair')}
              >
                {lang === 'en' ? 'Link with Phone Number' : 'Vincular con número'}
              </button>
            </div>

            {qrConnectMode === 'qr' ? (
              <>
                <p className="wa-link-help">
                  {lang === 'en'
                    ? 'Open WhatsApp on another device, go to Linked Devices, and scan the QR code.'
                    : 'Abre WhatsApp en otro dispositivo, ve a Dispositivos Vinculados y escanea el código QR.'}
                </p>
                <div className="wa-modal__actions">
                  <button type="button" className="wa-btn-cancel" onClick={() => setShowAdd(false)}>
                    {lang === 'en' ? 'Cancel' : 'Cancelar'}
                  </button>
                  <button type="button" className="wa-btn-connect" onClick={handleAdd} disabled={adding || !title.trim()}>
                    {adding
                      ? (lang === 'en' ? 'Generating...' : 'Generando...')
                      : (lang === 'en' ? 'Generate QR Code' : 'Generar Código QR')}
                  </button>
                </div>
              </>
            ) : (
              <div className="wa-pair">
                <p className="wa-pair__spot">
                  {lang === 'en'
                    ? "This is perfect when you're on your phone!"
                    : '¡Perfecto para cuando estás en tu teléfono!'}
                </p>
                <input
                  className="wa-modal__input"
                  type="text"
                  placeholder="+1 (786) 555-1234"
                  value={pairPhone}
                  onChange={(e) => setPairPhone(e.target.value)}
                  autoComplete="tel"
                />
                <div className="wa-modal__actions">
                  <button type="button" className="wa-btn-cancel" onClick={() => setShowAdd(false)}>
                    {lang === 'en' ? 'Cancel' : 'Cancelar'}
                  </button>
                  <button
                    type="button"
                    className="wa-btn-connect"
                    onClick={handleGetPairingCode}
                    disabled={pairLoading || !title.trim() || !pairPhone.trim()}
                  >
                    {pairLoading
                      ? (lang === 'en' ? 'Getting code…' : 'Obteniendo código…')
                      : pairCode
                        ? (lang === 'en' ? 'Get New Code' : 'Obtener nuevo código')
                        : (lang === 'en' ? 'Get Code' : 'Obtener Código')}
                  </button>
                </div>

                {pairCode ? (
                  <div className="wa-pair__codebox">
                    <div className="wa-pair__code">{pairCode}</div>
                    <div className="wa-pair__expiry">
                      {lang === 'en' ? 'Code expires in' : 'El código expira en'} {pairExpiresIn}s
                    </div>
                    <ol className="wa-pair__steps">
                      <li>{lang === 'en' ? 'Open WhatsApp on your phone' : 'Abre WhatsApp en tu teléfono'}</li>
                      <li>{lang === 'en' ? 'Go to Settings → Linked Devices → Link a Device' : 'Ve a Ajustes → Dispositivos Vinculados → Vincular dispositivo'}</li>
                      <li>{lang === 'en' ? "Tap 'Link with phone number'" : "Toca 'Vincular con número de teléfono'"}</li>
                      <li>{lang === 'en' ? 'Enter this code' : 'Ingresa este código'}</li>
                    </ol>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      )}

      {loading && (
        <div className="wa-loading"><div className="spinner" /></div>
      )}

      {!loading && hasManagedConnections && instances.length === 0 && !showOfficialProfileCard && (
        <div className="wa-empty">
          <div className="wa-empty__icon">📱</div>
          <h3>{lang === 'en' ? 'No WhatsApp profiles connected' : 'No hay perfiles WhatsApp conectados'}</h3>
          <p>{lang === 'en' ? 'Click "Connect New" to link your WhatsApp number' : 'Haz clic en "Conectar Nuevo" para vincular tu número'}</p>
        </div>
      )}

      {showDashboardHeader && (
        <div className="wa-grid">
          {showOfficialProfileCard && (
            <div className="wa-card wa-card--official-api" key="replytek-official-api">
              <div className="wa-card__header">
                <div className="wa-card__info wa-card__info--official">
                  <div className="wa-card__row-title">
                    <h3>
                      {officialApiStatus.business_name
                        || OFFICIAL_CARD_FALLBACK_TITLE[lang === 'en' ? 'en' : 'es']}
                    </h3>
                    <span
                      className="wa-card__badge-api"
                      title={lang === 'en' ? 'Official API' : 'API oficial'}
                    >
                      <Building2 className="wa-card__badge-api-icon" aria-hidden />
                      {lang === 'en' ? 'Official API' : 'API oficial'}
                    </span>
                  </div>
                  {officialApiStatus.display_phone_number && (
                    <span className="wa-card__number">
                      +
                      {String(officialApiStatus.display_phone_number).replace(/^\+/, '')}
                    </span>
                  )}
                  {officialApiStatus.waba_id && !officialApiStatus.business_name && (
                    <p className="wa-card__waba-hint">
                      WABA ID: {officialApiStatus.waba_id}
                    </p>
                  )}
                  {!officialApiStatus.loaded && officialApiConnected && (
                    <p className="wa-card__waba-hint">
                      {lang === 'en'
                        ? 'Loading connection details…'
                        : 'Cargando detalles de la conexión…'}
                    </p>
                  )}
                </div>
                <span className="wa-card__status wa-card__status--active">
                  <span className="wa-card__dot" />
                  <span className="wa-card__status-text">
                    {lang === 'en' ? 'Connected' : 'Conectado'}
                  </span>
                </span>
              </div>

              <div className="wa-card__connected">
                <span className="wa-card__check">✓</span>
                <p>
                  {lang === 'en'
                    ? 'WhatsApp Business API is connected and active'
                    : 'La API de WhatsApp Business está conectada y activa'}
                </p>
              </div>

              <div className="wa-card__footer">
                <span className="wa-card__date">
                  {lang === 'en' ? 'Meta Cloud API' : 'Meta Cloud API'}
                </span>
                <button
                  type="button"
                  className="wa-btn-delete"
                  onClick={() => void handleDisconnectOfficial()}
                  disabled={
                    disconnectingOfficial
                    || metaFlowLoading
                    || !canDisconnectOfficial
                  }
                >
                  {disconnectingOfficial
                    ? (lang === 'en' ? 'Disconnecting…' : 'Desconectando…')
                    : (lang === 'en' ? 'Disconnect' : 'Desconectar')}
                </button>
              </div>
            </div>
          )}

          {instances.map((inst) => {
            const st = statusLabel(inst.status);
            return (
              <div className="wa-card" key={inst.id}>
                <div className="wa-card__header">
                  <div className="wa-card__info">
                    <h3>{inst.title || 'WhatsApp'}</h3>
                    {inst.number && <span className="wa-card__number">+{String(inst.number).replace(/^\+/, '')}</span>}
                  </div>
                  <span className={statusClassForInstance(inst.status)}>
                    <span className="wa-card__dot" />
                    <span className="wa-card__status-text">{st.text}</span>
                  </span>
                </div>

                {inst.status === 'GENERATING' && inst.qr && (
                  <div className="wa-card__qr">
                    <p className="wa-card__qr-label">
                      {lang === 'en' ? 'Scan this QR code with WhatsApp' : 'Escanea este código QR con WhatsApp'}
                    </p>
                    <img src={inst.qr} alt="QR Code" className="wa-card__qr-img" />
                    <p className="wa-card__qr-hint">
                      {lang === 'en'
                        ? 'Open WhatsApp → Settings → Linked Devices → Link a Device'
                        : 'Abre WhatsApp → Ajustes → Dispositivos vinculados → Vincular dispositivo'}
                    </p>
                  </div>
                )}

                {inst.status === 'GENERATING' && !inst.qr && (
                  <div className="wa-card__qr wa-card__qr--loading">
                    <div className="spinner" />
                    <p>{lang === 'en' ? 'Generating QR code...' : 'Generando código QR...'}</p>
                  </div>
                )}

                {inst.status === 'ACTIVE' && (
                  <div className="wa-card__connected">
                    <span className="wa-card__check">✓</span>
                    <p>{lang === 'en' ? 'WhatsApp is connected and running' : 'WhatsApp está conectado y funcionando'}</p>
                  </div>
                )}

                {inst.status === 'INACTIVE' && (
                  <div className="wa-card__inactive">
                    <p>{lang === 'en' ? 'This profile is disconnected. Delete and reconnect.' : 'Este perfil está desconectado. Elimina y reconecta.'}</p>
                  </div>
                )}

                <div className="wa-card__footer">
                  <span className="wa-card__date">
                    {new Date(inst.createdAt).toLocaleDateString(lang === 'en' ? 'en-US' : 'es-ES', { year: 'numeric', month: 'short', day: 'numeric' })}
                  </span>
                  <button type="button" className="wa-btn-delete" onClick={() => handleDelete(inst.uniqueId)}>
                    {lang === 'en' ? 'Disconnect' : 'Desconectar'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showDashboardHeader && (
        <section className="wa-excluded" aria-labelledby="wa-excluded-title">
          <h2 id="wa-excluded-title" className="wa-excluded__title">
            {lang === 'en' ? 'Excluded Numbers' : 'Números Excluidos'}
          </h2>
          <p className="wa-excluded__sub">
            {lang === 'en'
              ? 'Add phone numbers that you don\'t want the AI bot to respond to. Messages from these numbers will still appear in your conversations, but the bot won\'t reply automatically.'
              : 'Agrega números de teléfono a los que no quieres que el bot de IA les responda. Los mensajes de estos números seguirán apareciendo en tus conversaciones, pero el bot no responderá automáticamente.'}
          </p>

          <div className="wa-excluded__form">
            <input
              type="text"
              className="wa-excluded__input"
              placeholder={lang === 'en' ? 'Phone number / Número de teléfono' : 'Número de teléfono / Phone number'}
              value={newExcludedPhone}
              onChange={(e) => setNewExcludedPhone(e.target.value)}
              autoComplete="tel"
            />
            <input
              type="text"
              className="wa-excluded__input"
              placeholder={
                lang === 'en'
                  ? 'Label (optional) — e.g. My personal number, Wife'
                  : 'Etiqueta (opcional) — ej. Mi número personal, Esposa'
              }
              value={newExcludedLabel}
              onChange={(e) => setNewExcludedLabel(e.target.value)}
            />
            <button
              type="button"
              className="wa-excluded__btn-add"
              onClick={() => void handleAddExcluded()}
              disabled={excludedSaving || !newExcludedPhone.trim()}
            >
              {excludedSaving
                ? '...'
                : lang === 'en'
                  ? 'Add'
                  : 'Agregar'}
            </button>
          </div>

          {excludedLoading ? (
            <p className="wa-excluded__muted">{lang === 'en' ? 'Loading…' : 'Cargando…'}</p>
          ) : excludedPhones.length === 0 ? (
            <p className="wa-excluded__empty">
              {lang === 'en' ? 'No excluded numbers' : 'No hay números excluidos'}
            </p>
          ) : (
            <ul className="wa-excluded__list">
              {excludedPhones.map((row) => (
                <li key={row.id} className="wa-excluded__row">
                  <div className="wa-excluded__row-main">
                    <span className="wa-excluded__num">+{String(row.phone).replace(/^\+/, '')}</span>
                    {row.label ? (
                      <span className="wa-excluded__label">{row.label}</span>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="wa-excluded__btn-remove"
                    onClick={() => void handleRemoveExcluded(row.id)}
                  >
                    {lang === 'en' ? 'Remove' : 'Eliminar'}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
